/**
 * Warehouse stock history viewmodels — the paginated ledger + the
 * current-year In-vs-Out chart, at product scope and variant scope. Both
 * compose usePaginatedList (ledger) + useCurrentYearStock (charts).
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import type { ProductPickerEntry } from '../models/product-variant.model'
import type { StockMovementDto, VariantOnHandDto, VariantPickerEntry } from '../models/stock.model'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'
import { useCurrentYearStock, type UseCurrentYearStockReturn } from './useCurrentYearStock'

export type { StockMovementDto, VariantOnHandDto, VariantPickerEntry } from '../models/stock.model'

interface LedgerBody<V> {
  readonly success: true
  readonly data: {
    readonly products: ProductPickerEntry[]
    readonly variants: V[]
    readonly ledger: { readonly items: StockMovementDto[]; readonly total: number }
  }
}

/* ───────────────── product scope ───────────────── */

export function useProductStockHistory(): {
  readonly productId: string | null
  readonly setProductId: (id: string | null) => void
  readonly organisationId: string | null
  readonly setOrganisationId: (id: string | null) => void
  readonly products: ProductPickerEntry[]
  readonly variants: VariantOnHandDto[]
  readonly ledger: StockMovementDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly chart: UseCurrentYearStockReturn
} {
  const { authedFetch } = useAuth()
  const [productId, setProductId] = useState<string | null>(null)
  // null = use the server-side default (master). Operators flip this
  // via the Organisation dropdown to inspect a tenant's pool.
  const [organisationId, setOrganisationId] = useState<string | null>(null)

  const paged = usePaginatedList<StockMovementDto, { products: ProductPickerEntry[]; variants: VariantOnHandDto[] }>({
    deps: [productId, organisationId],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        let url = `${APIROUTES.WAREHOUSEPRODUCTSTOCKHISTORY}?page=${page}&pageSize=${pageSize}`
        if (productId !== null && productId !== '') url += `&productId=${encodeURIComponent(productId)}`
        if (organisationId !== null && organisationId !== '') {
          url += `&organisation_id=${encodeURIComponent(organisationId)}`
        }
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as LedgerBody<VariantOnHandDto>
        return {
          items: body.data.ledger.items,
          total: body.data.ledger.total,
          extras: { products: body.data.products, variants: body.data.variants }
        }
      },
      [authedFetch, productId, organisationId]
    )
  })

  const chart = useCurrentYearStock('product', productId, organisationId)

  return {
    productId,
    setProductId,
    organisationId,
    setOrganisationId,
    products: paged.extras?.products ?? [],
    variants: paged.extras?.variants ?? [],
    ledger: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    chart
  }
}

/* ───────────────── variant scope ───────────────── */

export function useVariantStockHistory(): {
  readonly productId: string | null
  readonly setProductId: (id: string | null) => void
  readonly variantId: string | null
  readonly setVariantId: (id: string | null) => void
  readonly organisationId: string | null
  readonly setOrganisationId: (id: string | null) => void
  readonly products: ProductPickerEntry[]
  readonly variants: VariantPickerEntry[]
  readonly ledger: StockMovementDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly chart: UseCurrentYearStockReturn
} {
  const { authedFetch } = useAuth()
  const [productId, setProductId] = useState<string | null>(null)
  const [variantId, setVariantId] = useState<string | null>(null)
  const [organisationId, setOrganisationId] = useState<string | null>(null)

  const paged = usePaginatedList<StockMovementDto, { products: ProductPickerEntry[]; variants: VariantPickerEntry[] }>({
    deps: [productId, variantId, organisationId],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        let url = `${APIROUTES.WAREHOUSEVARIANTSTOCKHISTORY}?page=${page}&pageSize=${pageSize}`
        if (productId !== null && productId !== '') url += `&productId=${encodeURIComponent(productId)}`
        if (variantId !== null && variantId !== '') url += `&variantId=${encodeURIComponent(variantId)}`
        if (organisationId !== null && organisationId !== '') {
          url += `&organisation_id=${encodeURIComponent(organisationId)}`
        }
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as LedgerBody<VariantPickerEntry>
        return {
          items: body.data.ledger.items,
          total: body.data.ledger.total,
          extras: { products: body.data.products, variants: body.data.variants }
        }
      },
      [authedFetch, productId, variantId, organisationId]
    )
  })

  const chart = useCurrentYearStock('variant', variantId, organisationId)

  return {
    productId,
    setProductId,
    variantId,
    setVariantId,
    organisationId,
    setOrganisationId,
    products: paged.extras?.products ?? [],
    variants: paged.extras?.variants ?? [],
    ledger: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    chart
  }
}
